import { Moment } from "moment";
import { PageSizes } from "pdf-lib";
import { paperSizes } from "./constants";

export interface Attachment {
  file_name: string;
  status: string;
  _id: string;
  url?: string;
}

type Vendor = {
  vendor_name: string;
  manufacturer_name: string;
  part_number: string;
};

type OrganizationUnit = {
  unit_code: string;
  unit_location: string;
  other_details: string;
};

type TagInfo = {
  tag_desc: string;
  tag_group_color: string;
  tag_group_id: string;
  tag_group_canonical_name: string;
  tag_group_name: string;
  tag_name: string;
};

export interface PageContent {
  sequence_id: string;
  version: number;
  short_description?: string;
  group_name?: string;
  noun_name?: string;
  uom?: string;
  reference_id?: string;
  external_ref_id?: string;
  notes?: string;
  sourcing_type?: string;
  vendor?: Vendor;
  organization_unit?: OrganizationUnit;
  attachments?: Array<Attachment>;
  mainContent?: Attachment;
  serial_num?: string;
  normal_quantity?: number;
  spare_part: boolean;
  tagsInfo?: TagInfo[];
  spare_part_quantity?: number;
  bom?: PageContent;
  material?: PageContent;
  datasheets?: DataTable[];
}

export type PageSize = keyof typeof PageSizes;

export interface Alignment {
  vertical: string;
  horizontal: string;
}

export interface Position {
  x: number;
  y: number;
}

export type ContentType =
  | StaticContentType
  | ImageContentType
  | DynamicContentType;

export interface StaticContentType {
  content_type: "StaticContent";
  text: string;
  width?: number;
  height?: number;
}

export interface ImageContentType {
  content_type: "ImageContent";
  s3Key: string;
  height: number;
  width: number;
  text?: string;
  attachment_id?: string;
}

export interface DynamicContentField {
  display_name: string;
  field_name: string;
}

export interface DynamicContentType {
  content_type: "DynamicContent";
  fields: DynamicContentField[];
  object_type: string;
  width?: number;
  height?: number;
}

export const paginationFormatOptions = ["1 of N", "1/N", "1"] as const;

export type PaginationFormat = (typeof paginationFormatOptions)[number];

export interface ContentWithPositionRequest {
  content: ContentType;
  alignment: Alignment;
  position?: Position;
  is_active?: boolean;
}

export type Orientation = "portrait" | "landscape";

export type Margin = {
  border?: {
    bottom?: boolean;
    left?: boolean;
    right?: boolean;
    top?: boolean;
  };
  bottom_offset: number;
  top_offset: number;
  width_offset: number;
  is_active?: boolean;
};

export interface TemplateContent {
  _id: string;
  created_time: string;
  created_user: string;
  created_user_id: string;
  description: string;
  orientation?: Orientation;
  is_footer_active?: boolean;
  is_header_active?: boolean;
  footers: ContentWithPositionRequest[];
  headers: ContentWithPositionRequest[];
  left_annotation: {
    content: {
      text: string;
    };
    alignment: Alignment;
    position: Position;
    is_active?: boolean;
  };
  margin: Margin;
  name: string;
  page_number: {
    format: PaginationFormat;
    print: boolean;
    alignment: Alignment;
  };
  paper_size: string;
  right_annotation: {
    content: {
      text: string;
    };
    alignment: Alignment;
    position: Position;
    is_active?: boolean;
  };
  sequence_id?: string;
  version?: number;
  attachments: Attachment[];
  branding: {
    content?: ContentType;
    alignment: Alignment;
    position: Position;
  };
}

export type Dimension = { width: number; height: number };

export type PreviewImage = {
  url: string;
  dimension: Dimension;
  alignment: Alignment;
  isActive: boolean;
};

export type PaperSize = (typeof paperSizes)[number]["value"];

interface AttributeDetail {
  attr_name: string;
  attr_type: "Primary" | "Secondary" | "Tertiary"; // or whatever the possible values are
  is_mandatory: boolean;
  option_abbr: string;
  option_name: string;
  order: number;
}

interface SelectBoxOption {
  value: number | string | boolean;
  label: string;
  total_cost?: number;
}

interface AssociationObjectDetails {
  _id: string;
  sequence_id: string;
  version: string;
}

interface Association {
  object_type: 'bom' | 'document' | 'material' | 'product' | 'proposal' | 'project' | 'vendor' | 'issue' | 'instantSearch' | 'bomCost' | 'bomPricing' | 'bookMark' | 'quote' | 'dataTableForm' | 'task' | 'checklist' | 'dtTemplate',
  object_details: AssociationObjectDetails[];
}

export interface IBasicDetails {
  optionDetails: {
    label: string;
    value: string;
  }[];
  attribute_details: AttributeDetail[];
  notes: string;
  sequenceId: string;
  version: number;
  associations: Array<Association>;
  manufactureNo: string | number | boolean;
  supplierName: SelectBoxOption | null;
  manufactureName: string;
  short_description: string;
}

interface DTable {
  columns: {
    editable: boolean;
    field: string;
    header_class_name: string;
    header_name: string;
    width: number;
  }[];
  description: string;
  header_color: string;
  name: string;
  rows: {
    column_content: {
      content: string;
      field: string;
    }[];
    id: string;
  }[];
}

export interface DataTable {
  name: string;
  sequence_id: string;
  tables: DTable[];
}

export interface DocProps {
  title?: string;
  headerTitle?: string;
  orientation: Orientation;
  paperSize: PaperSize;
  dataSheet?: DataTable;
  basicDetails?: IBasicDetails;
}

export interface CostBreakupReportProps {
  title?: string;
  headerTitle?: string;
  orientation: Orientation;
  paperSize: PaperSize;
  bomDetails: Costing;
  pricingId: string;
  configState: PDFConfigState;
  showFooter: boolean;
  footerURL?: string;
}

export interface ExchangeRate {
  currency_code: string;
  manual_rate: number;
  market_rate: number;
}

export interface ExchangeData {
  base_currency: string;
  current_date: Moment | null | string;
  exchange_date: Moment | null | string;
  exchange_rates_auto: ExchangeRate[];
  exchange_rates_manual: ExchangeRate[];
  manual_rate: boolean;
  exchange_rates: ExchangeRate[];
}

export interface CostGroupItem {
  _id: number;
  description: string;
  value: number;
  quantity: number;
  total_cost: number;
  units: string;
}
export interface CostGroup {
  _id: number;
  title: string;
  value: number;
  discount_percentage: number;
  quantity: number;
  total_amount: number;
  costs: CostGroupItem[];
}
export interface CustomCost {
  _id: number;
  description: string;
  type: string;
  value: number;
  amount: number;
  subtract: boolean;
}

export interface Money {
  cost?: number;
  currency: string;
}

export interface AssemblyProcessCost {
  final_total_cost_in_pricing_currency: Money;
  proc_name: string;
  unit_cost: Money;
  quantity: number;
  custom_costs: CustomCost[];
  child_id?: string;
}

export interface MaterialCost {
  _id: string;
  child_id: string;
  markup_cost: number;
  markup_percentage: number;
  material_cost: Money;
  normal_quantity: number;
  override_cost?: number;
  sequence_id: string;
  short_description: string;
  spare_part_quantity: number;
  total_cost: Money;
  final_total_cost_in_parent_currency: Money;
  final_total_cost_in_pricing_currency: Money;
  total_custom_cost: number;
  total_custom_cost_fixed: number;
  total_custom_cost_percentage: number;
  total_quantity: number;
  unit_cost: number;
  version: number;
  custom_costs: CustomCost[];
}

export interface ChildrenCost {
  _id: string;
  assembly_process_costs: AssemblyProcessCost[];
  bom_cost: Money;
  child_id: string;
  children_costs: ChildrenCost[];
  material_costs: MaterialCost[];
  normal_quantity: number;
  override_cost?: number;
  sequence_id: string;
  short_description: string;
  spare_part_quantity: number;
  sum_of_assm_procs_total_cost: Money;
  sum_of_children_total_cost: Money;
  total_cost: Money;
  final_total_cost_in_parent_currency?: Money;
  final_total_cost_in_pricing_currency?: Money;
  sum_of_material_total_cost: Money;
  total_quantity: number;
  unit_cost: number;
  version: number;
}

export interface PricingConfig {
  _id: string;
  bom_cost_discount_percentage: number;
  margin_ratio: number;
  bom_cost_quantity: number;
  pricing_model: string;
  pricing_name: string;
  remarks: string;
  cost_groups: CostGroup[];
  discounts: CustomCost[];
  taxes: CustomCost[];
  other_costs: CustomCost[];
  exchange_rates: ExchangeData;
}

export interface BOMCost {
  _id: string;
  assembly_process_costs: AssemblyProcessCost[];
  bom_cost: Money;
  child_id: string;
  children_costs: ChildrenCost[];
  material_costs: MaterialCost[];
  normal_quantity: number;
  sequence_id: string;
  short_description: string;
  spare_part_quantity: number;
  sum_of_assm_procs_final_total_cost: Money;
  sum_of_assm_procs_final_total_cost_in_parent_currency: Money;
  sum_of_children_final_total_cost_in_parent_currency: Money;
  sum_of_children_final_total_cost_in_pricing_currency: Money;
  sum_of_material_final_total_cost_in_parent_currency: Money;
  sum_of_children_total_cost: Money;
  total_cost: Money;
  sum_of_material_total_cost: Money;
  total_quantity: number;
  unit_cost: number;
  version: number;
  final_total_cost: Money;
  final_total_cost_in_pricing_currency: Money;
}

export interface Costing {
  _id: string;
  calculated_cost: BOMCost;
  created_time: string;
  created_user: string;
  created_user_id: string;
  final_price: number;
  gross_cost: number;
  gross_cost_after_discount: number;
  gross_cost_after_margin: number;
  margin_amount: number;
  total_bom_cost: number;
  total_cost_group_cost: number;
  total_other_cost: number;
  total_discount: number;
  total_tax: number;
  sequence_id?: string;
  version?: number;
  test?: number;
  pricing_config: PricingConfig;
}

export interface PDFConfigState {
  levelLimit: SelectBoxOption,
  reportType: SelectBoxOption,
  hasOtherCost: boolean,
  hasAdjustment: boolean,
  hasTax: boolean,
  onlyBOM: boolean,
  hasNotes: boolean,
  tableHeaderColor: string,
  useMBFormatter: boolean,
  isNormalized: boolean,
  hasCostGroupItems: boolean,
};

export interface DataFormat {
  column_content: { field: string; content: string }[];
  id: string;
}

export interface ProcessedData {
  assembly_process_costs: DataFormat;
  children: DataFormat;
  parent: DataFormat;
  totalParentCost: number;
  chartData: Array<Array<string | number>>;
}

// Quote Types

export interface AddressValue {
  include: boolean;
  value?: string;
}
export interface Address {
  name: { include: boolean; value?: string };
  emailId: { include: boolean; value?: string };
  phoneNumber: { include: boolean; value?: string };
  address: { include: boolean; value?: string };
}

export interface QuoteItemDetail {
  id: string;
  entityType: SelectBoxOption | null;
  entity: SelectBoxOption | null;
  pricing: SelectBoxOption | null;
  partNumber: string;
  description: string;
  unitCost: number;
  quantity?: number;
  discount?: number;
  leadTime?: string;
  totalCost?: number;
}

export interface TaxDetail {
  id: string;
  description?: string;
  value?: number;
}

export interface CorrespondanceValue {
  include: boolean;
  address: Address;
  sameAs?: keyof Correspondance;
}

export interface Correspondance {
  soldBy: CorrespondanceValue;
  soldTo: CorrespondanceValue;
  billFrom: CorrespondanceValue;
  billTo: CorrespondanceValue;
  shipFrom: CorrespondanceValue;
  shipTo: CorrespondanceValue;
}

export interface CoverLetter {
  include: boolean;
  fromAddress?: string;
  fromAddressAs?: keyof Correspondance;
  subject?: string;
  subjectAsName?: boolean;
  content?: string;
  regards?: string;
  name?: string;
  designation?: string;
  attachments?: {
    _id: string;
    file_name: string;
    url: string;
    status: string;
  }[];
}

export interface TermsAndConditionDetail {
  id: string;
  heading?: string;
  notes?: string;
}

export interface QuoteCustomItemDetail {
  id: string;
  part_number?: string;
  description: string;
  unitCost: number;
  quantity: number;
  discount: number;
  leadTime: string;
  totalCost: number;
}

export interface CoverPageConfig {
  logo: {
    isActive: boolean,
    src: Attachment | File | null,
    vertical: number,
    horizontal: number,
    width: number,
    height: number,
    attachment_id?: string
  },
  include_cover_page: boolean,
  include_heading: boolean,
  heading: string,
  attachments?: Attachment[]
}

export type UserFields = { include: boolean, value: SelectBoxOption & { email?: string; user_id?: string; user_email?: string } | null };

interface User {
  user_email: string;
  user_id: string;
  user_name: string;
}

export interface RevisionHistoryInfo {
  _id: string;
  version_info: {
    date_time?: string,
    approver?: User;
    creator?: User;
    revisor?: User;
    description?: string;
  }
  // include_approver: boolean;
  // include_creator: boolean;
  // include_revisor: boolean;
  version: number;
}


export interface RevisionInfo {
  version?: number,
  date?: string,
  description: string;
  approved_by: UserFields;
  created_by: UserFields;
  referred_by: UserFields;
  history?: RevisionHistoryInfo[]
}

export interface QuoteSliceInterface {
  id: string;
  name: string;
  version: number;
  sequence_id: string;
  customQuote: boolean;
  revision_info: RevisionInfo,
  status?: SelectBoxOption | null;
  currency?: SelectBoxOption | null;
  tags?: SelectBoxOption[] | null;
  hfTemplate?: SelectBoxOption | null;
  creationDate?: Moment;
  validTill?: Moment;
  total_quote_price: number;
  total_quote_item_price: number;
  total_quote_tax_price: number;
  quote_item_details: QuoteItemDetail[];
  quote_custom_item_details: QuoteCustomItemDetail[];
  tax_details: TaxDetail[];
  correspondance: Correspondance;
  cover_letter: CoverLetter;
  terms_and_conditions: TermsAndConditionDetail[];
  contents: TermsAndConditionDetail[];
  include_cover_letter_from: boolean;
  include_cover_letter_subject: boolean;
  cover_page_config: CoverPageConfig;
  serial_num?: string,
  time_zone: string,
}

export interface QuoteReportProps {
  title?: string;
  headerTitle?: string;
  orientation: Orientation;
  paperSize: PaperSize;
  quoteDetails: QuoteSliceInterface;
  showFooter: boolean;
  footerURL?: string;
  configState: Partial<ConfigStateForPDF>;
  onlyCoverPage?: boolean;
  onlyHTML?: boolean
}

export interface ConfigStateForPDF {
  tableHeaderColor: string,
  useMBFormatter: boolean,
  include_serial_num: boolean,
  include_revision_table: boolean,
};

// Image Embedding types

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface RenderOptions {
  maxWidthPercentage?: number;  // Maximum width as percentage of page width
  maxHeightPercentage?: number; // Maximum height as percentage of page height
  preserveAspectRatio?: boolean;
  scale?: number;               // Scale factor for html2canvas
  format?: 'PNG' | 'JPEG';     // Image format
  quality?: number;            // Image quality (0-1)
}

export interface BOMReportPage {
  title?: string;
  headerTitle?: string;
  orientation: Orientation;
  paperSize: PaperSize;
  data: PageContent;
  showFooter: boolean;
  showBasicDetails?: boolean;
  showDataSheet?: boolean;
  footerURL?: string;
  onlyHTML?: boolean;
  imageDimension?: Dimension;
}
